import React, { useCallback, useEffect, useState } from "react";
import { Button, Container, Spinner } from "react-bootstrap";
import { ethers } from "ethers";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { MdToken, MdOpenInNew, MdRefresh } from "react-icons/md";
import ContractDetails from "../../Contracts/ContractDetails";
import GetChainId from "../../Common/GetChainId";
import { Data } from "../../Common/Data";
import "../TokenRateOnChain/TokenRateOnChain.css";

/** On-chain uint is the percent as a whole number: 50 = 50%. */
const MAX_PERCENT = 100;

function percentOnChainToDisplay(bn) {
  if (bn == null) return "—";
  try {
    return `${ethers.BigNumber.from(bn).toString()}%`;
  } catch {
    return "—";
  }
}

function displayInputToOnChainPercent(input) {
  const t = String(input).trim().replace(",", ".");
  if (t === "") throw new Error("empty");
  const n = parseFloat(t);
  if (!Number.isFinite(n) || n < 0) throw new Error("invalid");
  if (n > MAX_PERCENT) throw new Error("max 100");
  const rounded = Math.round(n * 100) / 100;
  return ethers.BigNumber.from(Math.round(rounded));
}

const StakeTokenPercentOnChain = () => {
  const account = useSelector((state) => state.accounts.value);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [contractOwner, setContractOwner] = useState(null);
  const [currentPercent, setCurrentPercent] = useState(null);
  const [newPercentStr, setNewPercentStr] = useState("");
  const [saving, setSaving] = useState(false);

  const loadChainData = useCallback(async () => {
    const { ethereum } = window;
    if (!ethereum || !account) {
      setLoading(false);
      return;
    }
    const ok = await GetChainId();
    if (!ok) {
      setLoading(false);
      return;
    }

    const provider = new ethers.providers.Web3Provider(ethereum);
    const contractRead = new ethers.Contract(
      ContractDetails.contract,
      ContractDetails.contractABI,
      provider
    );

    try {
      const [owner_, pct_] = await Promise.all([
        contractRead.owner(),
        contractRead.stakeTokenPercent(),
      ]);
      setContractOwner(owner_);
      setCurrentPercent(pct_);
    } catch (e) {
      console.error(e);
      toast.error(e?.reason || e?.message || "Failed to read contract");
      setCurrentPercent(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [account]);

  useEffect(() => {
    setLoading(true);
    loadChainData();
  }, [loadChainData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadChainData();
  };

  const handleSave = async () => {
    if (
      !contractOwner ||
      !account ||
      contractOwner.toLowerCase() !== account.toLowerCase()
    ) {
      toast.error("Only the contract owner wallet can update this value.");
      return;
    }
    let pctBn;
    try {
      pctBn = displayInputToOnChainPercent(newPercentStr);
    } catch {
      toast.error(
        "Enter a valid percentage from 0 to 100 (e.g. 50 for 50%)."
      );
      return;
    }

    const { ethereum } = window;
    if (!ethereum || !account) {
      toast.error("Connect your wallet first.");
      return;
    }
    const ok = await GetChainId();
    if (!ok) return;

    setSaving(true);
    try {
      const provider = new ethers.providers.Web3Provider(ethereum);
      const signer = provider.getSigner();
      const contractWrite = new ethers.Contract(
        ContractDetails.contract,
        ContractDetails.contractABI,
        signer
      );
      const tx = await contractWrite.setStakeTokenPercent(pctBn);
      toast.info("Confirm in your wallet…");
      await tx.wait();
      toast.success("Stake token percentage saved.");
      setNewPercentStr("");
      await loadChainData();
    } catch (e) {
      console.error(e);
      toast.error(
        e?.reason ||
          e?.data?.message ||
          e?.message ||
          "Could not save. Use the owner wallet."
      );
    } finally {
      setSaving(false);
    }
  };

  if (!account) {
    return (
      <Container fluid className="p-0">
        <div className="tr-page">
          <div className="tr-empty">
            <div className="tr-empty-icon" aria-hidden>
              <MdToken />
            </div>
            <h2>Connect wallet</h2>
            <p>
              Use the button in the top bar to connect your admin wallet, then
              you can view and update the stake token percentage.
            </p>
          </div>
        </div>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container fluid className="p-0">
        <div className="tr-page">
          <div className="tr-loading">
            <Spinner animation="border" variant="primary" role="status" />
            <p>Loading from contract…</p>
          </div>
        </div>
      </Container>
    );
  }

  const ownerMatch =
    contractOwner &&
    account &&
    contractOwner.toLowerCase() === account.toLowerCase();

  const displayCurrent = percentOnChainToDisplay(currentPercent);
  let rawDisplay = "—";
  try {
    if (currentPercent != null) {
      rawDisplay = currentPercent.toString();
    }
  } catch {
    rawDisplay = "—";
  }

  return (
    <Container fluid className="p-0">
      <div className="tr-page">
        <header className="tr-header">
          <h1 className="tr-title">Stake token percentage</h1>
          <p className="tr-subtitle">
            Read the live value from the main contract and publish updates. The
            stored number is the percent itself (50 means 50%).
          </p>
        </header>

        <div className="tr-toolbar">
          <Button
            variant="outline-secondary"
            className="d-inline-flex align-items-center gap-1"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <MdRefresh size={18} />
            {refreshing ? "Refreshing…" : "Refresh"}
          </Button>
          <Button
            variant="outline-primary"
            className="d-inline-flex align-items-center gap-1"
            href={`${Data.testnetLink}${ContractDetails.contract}`}
            target="_blank"
            rel="noreferrer"
          >
            <MdOpenInNew size={18} />
            Block explorer
          </Button>
        </div>

        <div className="tr-grid">
          <article className="tr-card">
            <div className="tr-card__head">
              <h2 className="tr-card__head-title">
                <MdToken aria-hidden />
                Current value
              </h2>
              <span className="tr-badge">On-chain</span>
            </div>
            <div className="tr-card__body">
              <div className="tr-rate-hero">
                <p className="tr-rate-label">Stake token percentage</p>
                <p className="tr-rate-value">{displayCurrent}</p>
                <p className="tr-hint" style={{ marginTop: "0.5rem" }}>
                  Raw (uint256): <code>{rawDisplay}</code>
                </p>
              </div>
            </div>
          </article>

          <article className="tr-card tr-form-card">
            <div className="tr-card__head">
              <h2 className="tr-card__head-title">
                <MdToken aria-hidden />
                {ownerMatch ? "Update percentage" : "Contract owner"}
              </h2>
            </div>
            <div className="tr-card__body">
              {!ownerMatch && contractOwner ? (
                <div className="tr-not-owner">
                  <p className="tr-not-owner-title">Owner wallet only</p>
                  <p className="tr-not-owner-address">{contractOwner}</p>
                  <p className="tr-not-owner-hint">
                    Your connected wallet is not the owner. Updates are
                    disabled. Connect with the address above to change the
                    percentage.
                  </p>
                  <div className="tr-connected-wallet">
                    Connected: <code>{account}</code>
                  </div>
                </div>
              ) : null}

              {!ownerMatch && !contractOwner ? (
                <p className="tr-hint">
                  Could not load contract owner. Try refresh or check the
                  network.
                </p>
              ) : null}

              {ownerMatch ? (
                <>
                  <div className="tr-field">
                    <label htmlFor="new-stake-pct">New percentage</label>
                    <input
                      id="new-stake-pct"
                      className="tr-input"
                      type="text"
                      inputMode="decimal"
                      placeholder="10"
                      value={newPercentStr}
                      onChange={(e) => setNewPercentStr(e.target.value)}
                      autoComplete="off"
                      disabled={saving}
                    />
                  </div>
                  <p className="tr-hint">
                    Enter the percent 0–100 (e.g. 50 for 50%). That value is sent
                    as-is to <code>setStakeTokenPercent</code>.
                  </p>
                  <button
                    type="button"
                    className="tr-save"
                    onClick={handleSave}
                    disabled={saving || !ownerMatch}
                  >
                    {saving ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          className="me-2"
                          role="status"
                        />
                        Saving…
                      </>
                    ) : (
                      "Save percentage"
                    )}
                  </button>
                </>
              ) : null}
            </div>
          </article>
        </div>
      </div>
    </Container>
  );
};

export default StakeTokenPercentOnChain;
